import * as React from 'react';
import { Alert, Button, Card, Col, Divider, Dropdown, Menu, message, Modal, Row, Select, Space, Spin, Typography } from 'antd';
import { ShortDateStringForUser } from '../../utilites/dateTimeHelper';
import { DateFormat } from '../../constants/dateFormat';
import { useParams } from 'react-router';
import { UserQueries } from '../../queries/user-queries';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/pro-regular-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import "./UserDetail.scss";
import { UserCompanyActiveStatus } from '../../models/user-profile-inteface';
import { ADMIN_APPLICATION_ID, APP_APPLICATION_ID } from '../../utilites/api';
import Paragraph from 'antd/lib/typography/Paragraph';
import { useState } from 'react';
import GrantAccessModal from './modals/GrantAccessModal';
import { GenerateCSTokenRequest } from '../../models/generate-cs-token-request-interface';
import { CountdownCircleTimer } from 'react-countdown-circle-timer'
import ActivityChartCard from '../../components/activity-chart/ActivityChartCard';
import UserCompanyCard from './components/UserCompanyCard';
import TwoFactorDeliveryHistoryCard from './components/TwoFactorDeliveryHistoryCard';
import { CompanyUserProfile } from '../../models/company-user-profile-interface';
import LockedAccountAlert from './LockedAccountAlert';
const { Option } = Select;

interface RouteParams {
  userId: string;
}

const tokenRefreshRateMinutes = 15;

function UserDetailPage() {
  const [grantAccessModalVisible, setGrantAccessModalVisible] = useState<boolean>(false);
  const [tokenTimeRemaining, setTokenTimeRemainder] = useState<number>(0);
  const { userId } = useParams<RouteParams>();
  const userCompanyProfilesQuery = UserQueries.useUserCompanyProfiles(Number(userId));
  const putUserActiveMutation = UserQueries.usePutUserActive(Number(userId));
  const passwordResetMutation = UserQueries.useResetPassword(userCompanyProfilesQuery.data?.[0]?.emailAddress ?? '');
  const passwordResetLinkMutation = UserQueries.usePasswordResetLink(userCompanyProfilesQuery.data?.[0]?.emailAddress ?? '');
  const grantAccessMutation = UserQueries.useGrantAccess(Number(userId));
  const customerServiceTokenQuery = UserQueries.useCustomerServiceToken(userCompanyProfilesQuery.data?.[0]?.csAccess ?? false, Number(userId));
  const revokeAccessMutation = UserQueries.useRevokeAccess(Number(userId));
  const userActivityQuery = UserQueries.useUserActivity(userCompanyProfilesQuery?.data?.[0]?.userId);
  const [companySort,setCompanySort] = useState<string>("all");
  const [roleSort,setRoleSort] = useState<string>("all");
  const [typeSort,setTypeSort] = useState<string>("all");

  const isLoading = userCompanyProfilesQuery.isLoading && userCompanyProfilesQuery.isFetching;
  const showRevokeAccess = userCompanyProfilesQuery.data?.[0]?.csAccess;
  const initialCircleCountdownTime = tokenRefreshRateMinutes - (new Date().getMinutes() % tokenRefreshRateMinutes);
  const { Text } = Typography;

  const onMenuClick = async (e) => {
    if (!!userCompanyProfilesQuery.data?.[0]?.emailAddress) {
      if (e.key === 'grantSupportAccess') {
        setGrantAccessModalVisible(true);
        return;
      }

      if (e.key === 'revokeSupportAccess') {
        const data = {
          userId: Number(userId),
          securityTokenTypeId: 2
        }
        await revokeAccessMutation.mutateAsync(data);
        return;
      }

      let applicationId;
      let plain = false;
      let apiCall = 'passwordReset';

      switch (e.key) {
        case 'passwordResetClient':
          applicationId = APP_APPLICATION_ID;
          break;
        case 'passwordResetAdmin':
          applicationId = ADMIN_APPLICATION_ID;
          break;
        case 'passwordResetClientPlainText':
          applicationId = APP_APPLICATION_ID;
          plain = true;
          break;
        case 'passwordResetAdminPlainText':
          applicationId = ADMIN_APPLICATION_ID;
          plain = true;
          break;
        case 'passwordResetLinkClient':
          applicationId = APP_APPLICATION_ID;
          apiCall = 'passwordResetLink';
          break;
        case 'passwordResetLinkAdmin':
          applicationId = ADMIN_APPLICATION_ID;
          apiCall = 'passwordResetLink';
          break;
      }
      
      apiCall === 'passwordReset'
        ? await passwordResetMutation.mutateAsync({ userName: userCompanyProfilesQuery.data?.[0]?.emailAddress, applicationId: applicationId, plain: plain })
        : await passwordResetLinkMutation.mutateAsync({ userName: userCompanyProfilesQuery.data?.[0]?.emailAddress, applicationId: applicationId }).then((url) => {
          Modal.info({
            title: 'Password Reset Link',
            content: (
              <Paragraph copyable>{url}</Paragraph>
            ),
            onOk() {},
            centered: true
          });
        })
    }
  }

  const menu = (
    <Menu
      onClick={onMenuClick}
    >
      <Menu.Item key="passwordResetClient">
        Password Reset Client
      </Menu.Item>
      <Menu.Item key="passwordResetAdmin">
        Password Reset Admin
      </Menu.Item>
      <Menu.Item key="passwordResetClientPlainText">
        Password Reset Client - Plain Text
      </Menu.Item>
      <Menu.Item key="passwordResetAdminPlainText">
        Password Reset Admin - Plain Text
      </Menu.Item>
      <Menu.Item key="passwordResetLinkClient">
        Password Reset Link - Client
      </Menu.Item>
      <Menu.Item key="passwordResetLinkAdmin">
        Password Reset Link - Admin
      </Menu.Item>
      <Menu.Item key={showRevokeAccess ? "revokeSupportAccess" : "grantSupportAccess"} danger>
        {showRevokeAccess ? "Revoke" : "Grant"} Support Access
      </Menu.Item>
    </Menu>
  );

  /**
   * Change active status
   * @param checked 
   */
  const onChange = async (checked: boolean, companyId: number) => {
    const data: UserCompanyActiveStatus = {
      companyId: companyId,
      active: checked
    }
    await putUserActiveMutation.mutateAsync(data);
  };

  /**
   * Grant access
   * @param values
   */
  const handleSubmit = async (values) => {
    const data: GenerateCSTokenRequest = {
      userId: Number(userId),
      reason: values.reason,
      expirationDaysFromNow: values.expirationDaysFromNow
    };
    await grantAccessMutation.mutateAsync(data);
    setGrantAccessModalVisible(false);
  }

  const copyEmail = () => {
    !!userCompanyProfilesQuery.data?.[0]?.emailAddress && navigator.clipboard.writeText(userCompanyProfilesQuery.data?.[0]?.emailAddress);
    message.success('Email copied');
  }

  const copyPassword = () => {
    !!customerServiceTokenQuery.data && navigator.clipboard.writeText(customerServiceTokenQuery.data.token);
    message.success('Password copied');
  }

  const filteredCompanies = ():CompanyUserProfile[] => {
    let data:CompanyUserProfile[] = userCompanyProfilesQuery.data ?? []
    var companies:CompanyUserProfile[] = []
    if(data.length > 0) {
      if(companySort === "all") {

        companies = data.filter(x => x.companyEnabled)
        companies = companies.concat(data.filter(x => !x.companyEnabled))
      } else if(companySort === "active") {
        companies = data.filter(x => x.companyEnabled)
      } else {
        companies = data.filter(x => !x.companyEnabled)
      }

      if(roleSort === "super") {
        companies = companies.filter(x => x.roleId === 2)
      } else if(roleSort === "admin") {
        companies = companies.filter(x => x.roleId === 3)
      } else if(roleSort === "ca") {
        companies = companies.filter(x => x.roleId === 1 && x.committeeUsers.some(cu => cu.committeeAdmin))
      } else if(roleSort === "standard") {
        companies = companies.filter(x => x.roleId === 1)
      }

      if(typeSort === "active") {
        companies = companies.filter(x => x.active)
      } else if(typeSort === "activenotified") {
        companies = companies.filter(x => x.active && !!x.notificationDate)
      } else if(typeSort === "inactive") {
        companies = companies.filter(x => !x.active)
      }
    }

    return companies
  }

  console.log(filteredCompanies())
  console.log(Array.isArray(filteredCompanies()))

  return (
    <>
      <Spin spinning={isLoading}>
        {userCompanyProfilesQuery.isError ||
        (!userCompanyProfilesQuery.data?.length && !isLoading) ? (
          <Alert
            type='error'
            message='Unable to retrieve user details. Please verify the user is valid.'
            showIcon
          />
        ) : (
          <Row gutter={[24, 24]}>
            <LockedAccountAlert user={userCompanyProfilesQuery.data?.[0]} isLoading={userCompanyProfilesQuery?.isFetching}/>
            <Col span={24}>
              <ActivityChartCard
                companyActivity={userActivityQuery.data}
                pageIsLoading={userCompanyProfilesQuery?.isLoading}
                chartIsLoading={
                  userActivityQuery.isLoading &&
                  !userCompanyProfilesQuery.isLoading
                }
              />
            </Col>
            <Col span={16}>
              <Row justify='end'>
                <Col>
                  <Space>
                    <Select
                      value={companySort}
                      onChange={(value: string) => setCompanySort(value)}
                    >
                      <Option key='all' value='all'>
                        All Companies
                      </Option>
                      <Option key='active' value='active'>
                        Active Companies
                      </Option>
                      <Option key='disabled' value='disabled'>
                        Disabled Companies
                      </Option>
                    </Select>
                    <Select
                      value={roleSort}
                      onChange={(value: string) => setRoleSort(value)}
                    >
                      <Option key='all' value='all'>
                        All Roles
                      </Option>
                      <Option key='super' value='super'>
                        Super User
                      </Option>
                      <Option key='admin' value='admin'>
                        Admin
                      </Option>
                      <Option key='ca' value='ca'>
                        Committee Admin
                      </Option>
                      <Option key='standard' value='standard'>
                        Standard User
                      </Option>
                    </Select>
                    <Select
                      value={typeSort}
                      onChange={(value: string) => setTypeSort(value)}
                    >
                      <Option key='all' value='all'>
                        All Types
                      </Option>
                      <Option key='active' value='active'>
                        Active
                      </Option>
                      <Option key='activenotified' value='activenotified'>
                        Active &amp; Notified
                      </Option>
                      <Option key='inactive' value='inactive'>
                        Inactive
                      </Option>
                    </Select>
                  </Space>
                </Col>
              </Row>
              <br />
              <Spin spinning={userCompanyProfilesQuery.isRefetching}>
                {filteredCompanies().map((x, i) => (
                  <React.Fragment key={i}>
                    <UserCompanyCard profile={x} onActiveChange={onChange} />
                  </React.Fragment>
                ))}
              </Spin>
            </Col>
            <Col span={8}>
              <Card
                className='user-detail-card'
                title={
                  <>
                    <h3 className='primary-text'>
                      <strong>
                        {userCompanyProfilesQuery.data?.[0]?.emailAddress}
                      </strong>
                    </h3>
                    <h4>USER #{userCompanyProfilesQuery.data?.[0]?.userId}</h4>
                  </>
                }
                bordered={false}
              >
                {!!customerServiceTokenQuery.data &&
                  userCompanyProfilesQuery.data?.[0]?.csAccess && (
                    <Alert
                      className='user-access-alert'
                      message={`Support has access to this account until ${ShortDateStringForUser(
                        customerServiceTokenQuery.data.expirationDate,
                        DateFormat.MonthDayYear
                      )}`}
                      description={
                        <Space className='full-width' direction='vertical'>
                          <Alert
                            message='The password expires every 15 minutes'
                            type='warning'
                          />
                          Password:
                          <Row
                            justify='space-between'
                            align='middle'
                            wrap={false}
                          >
                            <Col flex={'auto'}>
                              <Text
                                type={
                                  tokenTimeRemaining < 120
                                    ? 'danger'
                                    : undefined
                                }
                              >
                                {customerServiceTokenQuery.data.token}
                              </Text>
                            </Col>
                            <Col
                              className='countdown-timer-column'
                              flex={'50px'}
                            >
                              {!!initialCircleCountdownTime && (
                                <CountdownCircleTimer
                                  size={30}
                                  strokeWidth={5}
                                  isPlaying
                                  initialRemainingTime={
                                    !!initialCircleCountdownTime
                                      ? initialCircleCountdownTime * 60
                                      : undefined
                                  }
                                  duration={tokenRefreshRateMinutes * 60}
                                  colors={[
                                    '#004777',
                                    '#F7B801',
                                    '#A30000',
                                    '#A30000',
                                  ]}
                                  colorsTime={[7 * 60, 5 * 60, 2 * 60, 0]}
                                  onComplete={() => {
                                    customerServiceTokenQuery.refetch();
                                    return { shouldRepeat: true };
                                  }}
                                  onUpdate={(remainingTime) => {
                                    setTokenTimeRemainder(remainingTime);
                                  }}
                                >
                                  {({ remainingTime }) => <></>}
                                </CountdownCircleTimer>
                              )}
                            </Col>
                          </Row>
                          <Row gutter={8}>
                            <Col>
                              <Button
                                id='copyEmailButton'
                                type='primary'
                                onClick={copyEmail}
                              >
                                Copy Email
                              </Button>
                            </Col>
                            <Col>
                              <Button
                                id='copyPasswordButton'
                                type='primary'
                                onClick={copyPassword}
                              >
                                Copy Password
                              </Button>
                            </Col>
                          </Row>
                        </Space>
                      }
                      type='info'
                      showIcon
                    />
                  )}
                <Row justify='space-between'>
                  <Col>Last Login</Col>
                  <Col>
                    {ShortDateStringForUser(
                      userCompanyProfilesQuery.data?.[0]?.lastLoginDate ?? '',
                      DateFormat.MonthDayYear
                    )}
                  </Col>
                </Row>
                <Divider style={{ margin: '12px 0' }} />
                <Row justify='space-between'>
                  <Col>Password Changed</Col>
                  <Col>
                    {ShortDateStringForUser(
                      userCompanyProfilesQuery.data?.[0]?.passwordLastChanged ??
                        '',
                      DateFormat.MonthDayYear
                    )}
                  </Col>
                </Row>
                <Divider style={{ margin: '12px 0' }} />
                <Row justify='space-between'>
                  <Col>Two Factor</Col>
                  <Col>
                    {
                      userCompanyProfilesQuery.data?.[0]
                        ?.userPhoneAuthentication?.number
                    }
                  </Col>
                </Row>
                <Divider style={{ margin: '12px 0' }} />
                <Row>
                  <Dropdown overlay={menu}>
                    <Button
                      id='userDetailActionButton'
                      type='primary'
                      onClick={(e) => e.preventDefault()}
                      loading={
                        passwordResetMutation.isLoading ||
                        passwordResetLinkMutation.isLoading ||
                        grantAccessMutation.isLoading ||
                        revokeAccessMutation.isLoading
                      }
                    >
                      <Space>
                        Action
                        <FontAwesomeIcon icon={faAngleDown as IconProp} />
                      </Space>
                    </Button>
                  </Dropdown>
                </Row>
              </Card>
              {!!userCompanyProfilesQuery.data?.[0]?.userPhoneAuthentication?.number && (
                <TwoFactorDeliveryHistoryCard
                  userId={userCompanyProfilesQuery.data?.[0]?.userId}
                />
              )}
            </Col>
          </Row>
        )}
      </Spin>
      <GrantAccessModal
        isVisible={grantAccessModalVisible}
        setIsVisible={setGrantAccessModalVisible}
        handleSubmit={handleSubmit}
        grantAccessMutation={grantAccessMutation}
      />
    </>
  );
}

export default UserDetailPage;